import Header from "../components/Header"
import Footer from "../components/Footer"
import {
  Download,
  Settings,
  Play,
  Square,
  Edit,
  Trash2,
  ToggleRight,
  Monitor,
  Wifi,
  Key,
  Twitter,
  CheckCircle,
  FileText,
  Folder,
} from "lucide-react"

export default function BotManagerPage() {
  return (
    <div className="relative min-h-screen bg-white overflow-hidden">
      {/* Subtle animated background elements on white */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        {/* Very subtle blobs for texture */}
        <div className="blob blob-1 absolute h-[600px] w-[600px] rounded-full bg-gradient-to-br from-blue-50/30 via-blue-100/20 to-transparent blur-3xl"></div>
        <div className="blob blob-2 absolute h-[800px] w-[800px] rounded-full bg-gradient-to-tr from-blue-50/20 via-blue-100/20 to-transparent blur-3xl"></div>
        <div className="blob blob-3 absolute h-[500px] w-[500px] rounded-full bg-gradient-to-r from-blue-100/20 via-blue-50/20 to-transparent blur-3xl"></div>
        <div className="blob blob-4 absolute h-[700px] w-[700px] rounded-full bg-gradient-to-bl from-blue-100/20 via-blue-50/20 to-transparent blur-3xl"></div>

        {/* Very subtle shimmer effects */}
        <div className="shimmer shimmer-1 absolute h-[100px] w-[200px] opacity-5"></div>
        <div className="shimmer shimmer-2 absolute h-[150px] w-[300px] opacity-5"></div>
        <div className="shimmer shimmer-3 absolute h-[80px] w-[400px] opacity-5"></div>

        {/* Subtle particle dots */}
        <div className="absolute inset-0">
          {Array.from({ length: 30 }).map((_, i) => (
            <div
              key={i}
              className="star absolute rounded-full"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                backgroundColor: `rgba(59, 130, 246, ${Math.random() * 0.3 + 0.1})`,
                animationDuration: `${Math.random() * 3 + 2}s`,
              }}
            />
          ))}
        </div>
      </div>

      <Header />

      {/* Hero Section */}
      <div className="relative z-10 p-6 max-w-6xl mx-auto">
      <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium mb-4">
            <Download className="w-4 h-4" />
            <span>Desktop Application</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 mb-4">
            Hasbara.ai Bot Manager
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A powerful Windows application to run and manage multiple Twitter bots that detect and respond to
            anti-Israel content using advanced AI.
          </p>
        </div>

        {/* What is this Section */}
        <section className="mb-8">
          <div className="bg-white border border-blue-200 rounded-2xl p-6 shadow-lg shadow-blue-100/50 hover:shadow-blue-200/50 transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-blue-800">What is this?</h2>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed">
              This is a downloadable Windows application that helps you run and manage multiple Twitter bots that detect
              and respond to anti-Israel tweets using the Grok (xAI) API. Deploy your own network of AI-powered
              defenders.
            </p>
          </div>
        </section>

        {/* File Structure Section */}
        <section className="mb-8">
          <div className="bg-white border border-blue-200 rounded-2xl p-6 shadow-lg shadow-blue-100/50 hover:shadow-blue-200/50 transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Folder className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-blue-800">Included Files</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <pre className="text-gray-700 font-mono text-sm overflow-x-auto">
                {`├── 🚀 bot_manager_app.exe         ← Main program`}
              </pre>
            </div>
          </div>
        </section>

        {/* How to Run Section */}
        <section className="mb-8">
          <div className="bg-white border border-blue-200 rounded-2xl p-6 shadow-lg shadow-blue-100/50 hover:shadow-blue-200/50 transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Play className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-blue-800">How to Run</h2>
            </div>
            <div className="space-y-4">
              {[
                {
                  step: 1,
                  title: "Download",
                  description: "Click the button below to download the .exe file.",
                  icon: <Download className="w-5 h-5" />,
                },
                {
                  step: 2,
                  title: "Run the app",
                  description: "Double-click bot_manager_app.exe.",
                  icon: <Play className="w-5 h-5" />,
                },
                {
                  step: 3,
                  title: "Add a bot",
                  description: "Configure your first bot with the required credentials.",
                  icon: <Settings className="w-5 h-5" />,
                },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full text-white font-bold text-sm">
                    {item.step}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="text-blue-600">{item.icon}</div>
                      <h3 className="font-semibold text-blue-800">{item.title}</h3>
                    </div>
                    <p className="text-gray-700">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Configuration Details */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-blue-800 mb-3">Required Configuration Fields:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { field: "name", desc: "A short nickname for your bot" },
                  { field: "api_server_url", desc: "https://hasbara-ai.com" },
                  { field: "llm_key", desc: "Your Grok (xAI) API key" },
                  { field: "x_api_key", desc: "Twitter API key" },
                  { field: "x_api_secret", desc: "Twitter API secret" },
                  { field: "x_access_token", desc: "Twitter access token" },
                  { field: "x_access_secret", desc: "Twitter access secret" },
                  { field: "query", desc: "Twitter search string" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <code className="bg-blue-100 px-2 py-1 rounded text-blue-800 font-mono">{item.field}</code>
                    <span className="text-gray-400">–</span>
                    <span className="text-gray-600">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Controls & Features Section */}
        <section className="mb-8">
          <div className="bg-white border border-blue-200 rounded-2xl p-6 shadow-lg shadow-blue-100/50 hover:shadow-blue-200/50 transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Monitor className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-blue-800">Controls & Features</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  icon: <Play className="w-5 h-5" />,
                  label: "Start",
                  desc: "Launches the bot loop",
                  color: "bg-green-600",
                },
                {
                  icon: <Square className="w-5 h-5" />,
                  label: "Stop",
                  desc: "Stops the bot immediately",
                  color: "bg-red-600",
                },
                {
                  icon: <Edit className="w-5 h-5" />,
                  label: "Edit",
                  desc: "Modify a bot's configuration",
                  color: "bg-blue-600",
                },
                {
                  icon: <Trash2 className="w-5 h-5" />,
                  label: "Delete",
                  desc: "Permanently remove a bot",
                  color: "bg-red-600",
                },
                {
                  icon: <ToggleRight className="w-5 h-5" />,
                  label: "Cynical Mode",
                  desc: "Toggle between factual/cynical responses",
                  color: "bg-purple-600",
                },
                {
                  icon: <FileText className="w-5 h-5" />,
                  label: "Log Window",
                  desc: "Real-time bot status and events",
                  color: "bg-gray-600",
                },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className={`p-2 ${item.color} rounded-lg`}>{item.icon}</div>
                  <div>
                    <h3 className="font-semibold text-blue-800">{item.label}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Screenshot Section */}
        <div className="mb-12">
          <div className="bg-white border border-blue-200 rounded-2xl p-6 shadow-xl shadow-blue-100/50 hover:shadow-blue-200/50 transition-shadow">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-blue-800 mb-2">Application Preview</h3>
              <p className="text-gray-600">See what the Bot Manager looks like in action</p>
            </div>

            {/* Image Frame */}
            <div className="relative bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
              {/* Decorative corners */}
              <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-blue-400 rounded-tl-lg"></div>
              <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-blue-400 rounded-tr-lg"></div>
              <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-blue-400 rounded-bl-lg"></div>
              <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-blue-400 rounded-br-lg"></div>

              {/* Image container with shadow */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <img
                  src="/bot-manager-screenshot.jpg"
                  alt="Hasbara.ai Bot Manager Application Interface"
                  className="w-full h-auto"
                />
              </div>

              {/* Subtle glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/5 via-transparent to-blue-400/5 rounded-xl pointer-events-none"></div>
            </div>

            {/* Caption */}
            <div className="text-center mt-4">
              <p className="text-sm text-gray-500 italic">
                The Bot Manager interface showing bot status, controls, and real-time logs
              </p>
            </div>
          </div>
        </div>

        {/* Requirements Section */}
        <section className="mb-8">
          <div className="bg-white border border-blue-200 rounded-2xl p-6 shadow-lg shadow-blue-100/50 hover:shadow-blue-200/50 transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-600 rounded-lg">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-blue-800">Requirements</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: <Monitor className="w-5 h-5" />, text: "Windows 10 or 11 (64-bit)" },
                { icon: <Wifi className="w-5 h-5" />, text: "Internet connection" },
                { icon: <Key className="w-5 h-5" />, text: "A Grok (xAI) API Key" },
                { icon: <Twitter className="w-5 h-5" />, text: "Twitter Developer account" },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="text-blue-600">{item.icon}</div>
                  <span className="text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Download CTA */}
        <section className="text-center">
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 rounded-2xl p-8 shadow-xl shadow-blue-200/50">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Deploy Your Bots?</h2>
            <p className="text-blue-100 text-lg mb-6 max-w-2xl mx-auto">
              Download the Hasbara.ai Bot Manager and start defending Israel's narrative on Twitter with AI-powered
              responses.
            </p>
            <a
              href="/static/bot_manager_app.exe"
              download
              className="w-fit bg-white text-blue-700 px-6 py-3 rounded-full font-bold text-lg hover:bg-blue-50 transition-all hover:shadow-xl hover:-translate-y-1 flex items-center gap-2 mx-auto"
            >
              <Download className="w-5 h-5" />
              Download Bot Manager
            </a>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  )
}
